/**
 * `/runs/:id` — run detail page (M3.5 polish, RLY-025).
 *
 * The body is composed from five focused components landed in M3.5:
 *   1. `<RunTimeline>`     — vertical event trail derived from the run object.
 *   2. `<RunTranscript>`   — conversational transcript (text + tool_use anchors).
 *   3. `<RunMcpTable>`     — one row per tool_use block with input/output detail.
 *   4. `<RunCostBreakdown>`— grouped cost events (terminal-only fetch).
 *   5. `<RunDeveloperDetails>` — collapsed raw JSON for power users.
 *
 * Header (status badge, agent name + version, timestamps, duration) and the
 * disabled Phase-1 Cancel button stay here. Live polling continues to flow
 * from `useRunDetail` (`refetchInterval` stops on terminal status).
 *
 * Cross-package wires:
 *   - Schemas: `RunResponse` from `@simpliwork/api-schema`.
 *   - API: `apps/portal-api/src/routes/runs.ts` (`GET /api/v1/runs/:id` and
 *     `GET /api/v1/runs/:id/cost-events`).
 *   - Hooks: `useRunDetail`, `useRunCostEvents` from `hooks/use-runs.ts`.
 */
import { Link, useParams } from "react-router-dom";

import { RunCostBreakdown } from "@/components/run-cost-breakdown";
import { RunDeveloperDetails } from "@/components/run-developer-details";
import { RunMcpTable } from "@/components/run-mcp-table";
import { RunTimeline } from "@/components/run-timeline";
import { RunTranscript } from "@/components/run-transcript";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAgentDetail } from "@/hooks/use-agents";
import { isTerminalRunStatus, useRunCostEvents, useRunDetail } from "@/hooks/use-runs";
import {
  formatCostMicros,
  formatDurationMs,
  formatTimestamp,
  runDurationMs,
  runStatusToBadgeVariant,
} from "@/lib/formatters";

export function RunDetailPage() {
  const { id } = useParams();
  const run = useRunDetail(id);
  // We pull the agent detail to show name + version pill in the header. Two
  // queries instead of one keep the hooks composable; the agent record is
  // cached separately and rarely refetches.
  const agentId = run.data?.agent_id;
  const agent = useAgentDetail(agentId);
  // Cost events fire as a one-shot terminal-only fetch. The hook itself gates
  // on `isTerminalRunStatus(runStatus)` — passing the live status keeps the
  // gate honest as the run flips to terminal.
  const costEvents = useRunCostEvents(id, run.data?.status);

  if (run.isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="run-detail-loading">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (run.error) {
    return (
      <Card data-testid="run-detail-error">
        <CardContent className="pt-6">
          <p className="text-sm text-destructive">Couldn't load run: {run.error.message}</p>
          <Link to="/dashboard">
            <Button variant="outline" size="sm" className="mt-4">
              Back to dashboard
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  if (!run.data) return null;

  const data = run.data;
  const isRunning = !isTerminalRunStatus(data.status);
  const duration = runDurationMs({
    started_at: data.started_at,
    completed_at: data.completed_at,
  });

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="run-detail-heading">
              Run <span className="font-mono text-xl">{data.id.slice(0, 8)}</span>
            </h1>
            <Badge variant={runStatusToBadgeVariant(data.status)} data-testid="run-detail-status">
              {data.status}
            </Badge>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {agent.data ? (
              <Link
                to={`/agents/${agent.data.id}/configure`}
                className="inline-flex items-center gap-2 underline-offset-4 hover:underline"
                data-testid="run-detail-agent-link"
              >
                <span>{agent.data.name}</span>
                <Badge variant="secondary" data-testid="run-detail-agent-version">
                  v{data.agent_version}
                </Badge>
              </Link>
            ) : (
              <span data-testid="run-detail-agent-loading">
                Agent <span className="font-mono text-xs">{data.agent_id.slice(0, 8)}</span>
                {" · "}
                <Badge variant="secondary" data-testid="run-detail-agent-version">
                  v{data.agent_version}
                </Badge>
              </span>
            )}
            <span data-testid="run-detail-backend">Backend: {data.backend}</span>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-4">
            <div>
              <dt className="font-medium">Created</dt>
              <dd data-testid="run-detail-created-at">{formatTimestamp(data.created_at)}</dd>
            </div>
            <div>
              <dt className="font-medium">Started</dt>
              <dd data-testid="run-detail-started-at">
                {data.started_at ? formatTimestamp(data.started_at) : "—"}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Completed</dt>
              <dd data-testid="run-detail-completed-at">
                {data.completed_at ? formatTimestamp(data.completed_at) : "—"}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Duration</dt>
              <dd data-testid="run-detail-duration">{formatDurationMs(duration)}</dd>
            </div>
          </dl>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span data-testid="run-detail-cost-total">
              Total cost:{" "}
              <span className="font-semibold text-foreground tabular-nums">
                {formatCostMicros(data.usage.cost_micros)}
              </span>
            </span>
            {data.cost_finalized ? (
              <Badge variant="success" data-testid="run-detail-cost-finalized">
                Finalized
              </Badge>
            ) : (
              <Badge variant="secondary" data-testid="run-detail-cost-pending">
                Pending finalize
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-start gap-2">
          {/*
            Phase-1 cancel UX (AC #6): button present but disabled with a
            tooltip explaining the Phase-4 dependency. We use the native
            `title` attribute for the tooltip — Sprint-06 hand-rolled-shadcn
            doesn't ship a Radix `<Tooltip>` and the polish bar accepts the
            native fallback per the existing primitive set.
          */}
          <Button
            variant="outline"
            size="sm"
            disabled
            data-testid="run-detail-cancel-cta"
            title="Cancellation is supported in Phase 4 for this backend."
            aria-label="Cancel run (disabled in Phase 1)"
          >
            Cancel
          </Button>
        </div>
      </header>

      {/* Polling indicator — visible only while the run is non-terminal. */}
      {isRunning && (
        <div
          data-testid="run-detail-polling-indicator"
          aria-live="polite"
          className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-muted-foreground"
        >
          Live — polling every second while the run is{" "}
          <span className="font-medium text-foreground">{data.status}</span>.
        </div>
      )}

      {/*
        Error banner — preserved from Phase-1. Renders only when the run has
        a structured error envelope. The new component stack below still
        renders for failed runs (Timeline shows "Failed", DeveloperDetails
        exposes the raw error JSON), so this banner is the user-facing summary.
      */}
      {data.error && (
        <Card data-testid="run-detail-error-card">
          <CardHeader>
            <CardTitle className="text-destructive">Error</CardTitle>
            <CardDescription>The run terminated with a structured error envelope.</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 gap-3 text-sm">
              <div>
                <dt className="font-medium text-muted-foreground">Code</dt>
                <dd data-testid="run-detail-error-code">
                  <code className="rounded bg-destructive/10 px-1.5 py-0.5 text-xs text-destructive">
                    {data.error.code}
                  </code>
                </dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Message</dt>
                <dd
                  data-testid="run-detail-error-message"
                  className="whitespace-pre-wrap text-destructive"
                >
                  {data.error.message}
                </dd>
              </div>
              {data.error.retryable !== undefined && (
                <div>
                  <dt className="font-medium text-muted-foreground">Retryable</dt>
                  <dd data-testid="run-detail-error-retryable">
                    {data.error.retryable ? "Yes" : "No"}
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
      )}

      <RunTimeline run={data} />
      <RunTranscript run={data} />
      <RunMcpTable run={data} />
      <RunCostBreakdown runId={data.id} runStatus={data.status} />
      <RunDeveloperDetails run={data} costEvents={costEvents.data} />
    </div>
  );
}

export default RunDetailPage;
